import { supabase } from '../lib/supabase-client';
import type { AIAssistantRequest, AIAssistantResponse, AIAction } from '../types/ai-assistant-types';

/**
 * Send a message to the KaiMed AI assistant via Supabase Edge Function.
 * The edge function calls Claude API with the patient's message and context.
 */
export async function sendAIMessage(
  request: AIAssistantRequest,
): Promise<AIAssistantResponse> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const response = await fetch(
    `${supabaseUrl}/functions/v1/ai-assistant`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        message: request.message,
        conversation_history: request.conversation_history,
        patient_context: {
          current_page: request.context.current_page,
          current_page_title: request.context.current_page_title,
          patient_id: request.context.patient_id,
        },
      }),
    },
  );

  if (!response.ok) {
    const errText = await response.text();
    console.error('AI assistant error:', response.status, errText);
    throw new Error(`AI assistant request failed: ${response.status}`);
  }

  const data = await response.json();

  // Map edge function response to the expected AIAssistantResponse shape
  const actions: AIAction[] = (data.actions || []).map((a: { label: string; path: string }) => ({
    type: 'navigate' as const,
    label: a.label,
    payload: { path: a.path },
  }));

  return {
    message: data.response,
    actions,
    suggestions: data.suggestions || [],
    metadata: {
      model: 'claude-sonnet-4-20250514',
      disclaimer: data.disclaimer,
    },
  };
}
